/**
 * Iter 74 Phase 3 — Offline write queue v2.
 *
 * A pragmatic, content-safe queue for mutations that must survive
 * tab refresh + going offline.
 *
 * Storage: localStorage. (IndexedDB is more capacious but harder to
 * audit + recover from corrupt state in the field. A few hundred KB
 * of pending mutations fit comfortably in localStorage.)
 *
 * Each queue entry:
 *   {
 *     id: string,             // local-only ID, never sent to server
 *     op: "markup.create" | "markup.delete",
 *     payload: any,           // mutation body
 *     queued_at: ISO string,
 *     attempts: number,       // informational retry counter — NEVER
 *                              // gates retry by itself (see GH-07 below)
 *     permanent: boolean,     // GH-07 — the ONLY thing that stops retry
 *     last_error?: string,
 *     owner: { tenant_id, user_id } | null,  // FP-031 — stamped identity
 *     client_op_id: string,   // FP-034 — server idempotency key
 *   }
 *
 * Auto-sync:
 *   - On `online` event.
 *   - On import (cheap retry if browser was offline at load).
 *   - Every 30s ticker.
 *
 * Conflict handling:
 *   - 404 on delete (markup was already gone server-side) → drop silently.
 *   - 401 (session expired) → FP-030 fix: PAUSE the flush entirely, keep
 *     every remaining entry untouched (never dead-lettered, never
 *     dropped). A field user's session expiring mid-shift must never
 *     cost them queued work — it retries automatically once they
 *     reauthenticate (next `online`/interval tick after a fresh token
 *     is present).
 *   - 4xx other than 401/404/429 → the ONLY case that marks `permanent:
 *     true` (kept in queue, visible, but never auto-retried again — an
 *     actionable rejection the user must resolve, e.g. drop or escalate).
 *   - 5xx / network errors / any other transient failure → GH-07 (real-
 *     plan certification, 2026-09-30): these increment `attempts`
 *     (informational only, shown to the user) but NEVER set `permanent`.
 *     Reproduced: 5 consecutive transient failures during an outage
 *     used to push `attempts` to the same threshold `flush()` used to
 *     skip entries forever — restoring connectivity after exactly 5
 *     dropped flushes left the record permanently stuck, needing a
 *     manual "retry failed" click the user had no reason to know about.
 *     A transient failure is now retried on every tick indefinitely
 *     until it either succeeds or the SERVER gives an actionable
 *     permanent rejection — connectivity coming back always eventually
 *     saves it with no manual action required.
 *
 * FP-031 (identity ownership): every entry is stamped with the
 * tenant_id/user_id decoded from the CURRENT session's access token at
 * enqueue time. `flush()`/`listQueue()`/`queueState()` only ever act on
 * entries owned by the CURRENTLY authenticated identity — a different
 * user (or the same user after a fresh, different-session login) on
 * the same physical device can never see or trigger replay of another
 * identity's queued mutations. Legacy entries with no `owner` (queued
 * before this fix shipped) are treated as belonging to whichever
 * identity is currently logged in, once, then get the same protection
 * going forward.
 */
import { api } from "@/lib/api";

const KEY = "fop_offline_queue_v2";
let _tick = null;
let _running = false;

// R-04 (independent recheck, 2026-10-01) — original GH-19.
//
// Root cause: `flush()` processes one queued entry at a time, fully
// `await`-ing the server create before removing it from the queue.
// `dropEntry` (the "Undo" button's handler) re-reads the queue and
// removes an entry by id — with NO awareness that `flush()` might, at
// that exact moment, already have SENT that entry's create request to
// the server and be waiting on the response. Undo reported success,
// the in-flight create still completed server-side, and nothing ever
// told the server to take it back — the markup silently "resurrected"
// the next time anyone (the same device or a different session)
// reloaded that sheet, because the server's data was always the
// actual truth and the queue's "it's gone" was a LOCAL-ONLY fiction.
//
// Fix: every entry is marked `in_flight: true` the instant `flush()`
// hands it to `applyOne`. If `dropEntry` sees `in_flight` on a
// compensable create op, it still removes it immediately (Undo must
// feel instant), but records a durable tombstone keyed by the entry's
// own `client_op_id` first. When that in-flight `applyOne` eventually
// resolves, `flush()` checks for a tombstone before treating the
// result as a normal success — if one exists, it issues an idempotent
// compensating DELETE against the exact object the server just
// created (never anything else), clears the tombstone, and never
// surfaces that object to the UI. `restoreEntry` ("undo the undo")
// clears a pending tombstone too, so a create that's still genuinely
// in flight when the user changes their mind a second time is kept,
// not deleted out from under them.
const TOMBSTONE_KEY = "fop_offline_cancel_tombstones_v1";

// Only "*.create" ops produce a server object that could need
// compensating; every one of these has a real, verified DELETE route.
const _COMPENSATING_DELETE = {
  "markup.create": (result) => api.delete(`/markups/${result.id}`),
  "object.create": (result) => api.delete(`/objects/${result.id}`),
  "punch.create": (result) => api.delete(`/punch-items/${result.id}`),
  "pin.create": (result) => api.delete(`/pins/${result.id}`),
};

function _loadTombstones() {
  if (typeof window === "undefined") return { ..._tombstoneMemoryFallback };
  let stored = {};
  try { stored = JSON.parse(window.localStorage.getItem(TOMBSTONE_KEY) || "{}"); }
  catch { stored = {}; }
  // B3-06 (2026-10-01 completion directive) — if a PREVIOUS
  // `_saveTombstones` call this page load failed to persist (quota,
  // private mode, disabled storage), the tombstone it was trying to
  // save must still be visible to flush()/_retryPendingCancellations
  // for as long as THIS page is alive — merging the in-memory
  // fallback over the (possibly stale) stored value prevents a
  // cancellation intent from silently vanishing the instant
  // persistence first fails.
  return { ...stored, ..._tombstoneMemoryFallback };
}
const _tombstoneMemoryFallback = {};
// Returns true only if the write actually reached durable storage.
function _saveTombstones(t) {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(t));
    for (const k of Object.keys(_tombstoneMemoryFallback)) delete _tombstoneMemoryFallback[k];
    return true;
  } catch {
    Object.assign(_tombstoneMemoryFallback, t);
    return false;
  }
}
// Returns true only if the tombstone reached durable storage —
// `dropEntry` surfaces this so the UI can warn the user not to close
// the tab/app before it syncs, instead of silently implying the
// cancellation is safe when it currently only lives in this tab's
// memory.
//
// B3-06 (2026-10-01 completion directive): stores `payload` alongside
// `op`/`client_op_id` — if the in-flight create's OWN response is lost
// to an ambiguous (non-definitive) error, the entry that was dropped
// from the visible queue no longer exists anywhere else; this is the
// ONLY remaining record of what to idempotently retry so the real
// outcome (did it land or not) gets resolved by an actual answer
// rather than assumed away. See `_retryAmbiguousCreates` below.
function _addTombstone(clientOpId, op, owner, payload) {
  if (!clientOpId) return true;
  const t = _loadTombstones();
  t[clientOpId] = { op, owner: owner || null, payload: payload || null, tombstoned_at: new Date().toISOString() };
  return _saveTombstones(t);
}
function _popTombstone(clientOpId) {
  if (!clientOpId) return false;
  const t = _loadTombstones();
  if (!(clientOpId in t)) return false;
  delete t[clientOpId];
  delete _tombstoneMemoryFallback[clientOpId];
  _saveTombstones(t);
  return true;
}
// B2-08 (independent recheck batch 2, 2026-10-01) — the ORIGINAL
// flush() path called `_popTombstone` (deleting the cancellation
// record) BEFORE the compensating DELETE was even attempted, then
// best-effort-swallowed any failure of that DELETE in a bare `catch`.
// The result: if the compensating DELETE failed, timed out, or its
// response was lost, the object stayed alive on the server forever,
// with zero durable record anywhere that a cancellation was ever
// owed for it — Undo had already told the user "cancelled" and the
// object never reached the UI either way (see the `continue` below),
// so nothing would ever prompt a retry. `_markTombstonePending`
// upgrades (never deletes) the SAME tombstone with exactly which
// server object still needs deleting, so a later flush pass — or
// `retryPendingCancellations()` below — can find it and retry the
// idempotent DELETE until it is actually confirmed (2xx or 404,
// "already gone" counts as success) or a human intervenes.
function _markTombstonePending(clientOpId, resultId) {
  if (!clientOpId) return;
  const t = _loadTombstones();
  const existing = t[clientOpId];
  if (!existing) return;
  t[clientOpId] = {
    ...existing,
    result_id: resultId,
    delete_attempts: (existing.delete_attempts || 0) + 1,
    last_delete_attempt_at: new Date().toISOString(),
  };
  _saveTombstones(t);
}
// Attempts every tombstone that already knows WHICH server object it
// owes a delete for (i.e. the create landed, but the compensating
// DELETE has not yet been confirmed). Idempotent: a 404 means the
// object is already gone — treated as success, not failure. Safe to
// call from any flush() pass; entries with no `result_id` yet (the
// create itself hasn't resolved) are left untouched.
async function _retryPendingCancellations(owner) {
  const t = _loadTombstones();
  for (const [clientOpId, rec] of Object.entries(t)) {
    if (!rec || !rec.result_id) continue;
    if (owner && rec.owner && !_sameOwner(rec.owner, owner)) continue;
    const compensate = _COMPENSATING_DELETE[rec.op];
    if (!compensate) { _popTombstone(clientOpId); continue; }
    try {
      await compensate({ id: rec.result_id });
      _popTombstone(clientOpId);
    } catch (e) {
      if (e?.response?.status === 404) {
        _popTombstone(clientOpId); // already gone server-side — confirmed done.
      } else {
        _markTombstonePending(clientOpId, rec.result_id); // keep retrying next pass.
      }
    }
  }
}

// B3-06 (2026-10-01 completion directive) — the counterpart to
// `_retryPendingCancellations` above: tombstones whose create outcome
// is STILL genuinely unknown (no `result_id` yet — the first attempt's
// response was lost to an ambiguous, non-definitive error) are
// idempotently retried HERE, using the exact `client_op_id` the
// original attempt used. Whatever answer comes back this time is the
// real, confirmed answer — it either reveals the create already
// landed (the server's own idempotent dedup hands back the existing
// object) or that it's only now actually being created; either way
// `result_id` gets set and `_retryPendingCancellations` (called right
// after, in `flush()`) immediately acts on it. Only a DEFINITIVE
// rejection (4xx) justifies concluding it will never land.
async function _retryAmbiguousCreates(owner) {
  const t = _loadTombstones();
  for (const [clientOpId, rec] of Object.entries(t)) {
    if (!rec || rec.result_id || !rec.payload) continue; // already resolved, or nothing to replay
    if (owner && rec.owner && !_sameOwner(rec.owner, owner)) continue;
    try {
      const result = await applyOne({ op: rec.op, payload: rec.payload, client_op_id: clientOpId });
      _markTombstonePending(clientOpId, result?.id || null);
    } catch (e) {
      const code = e?.response?.status;
      if (code && code >= 400 && code < 500 && code !== 429) {
        _popTombstone(clientOpId); // confirmed by the server itself: this will never land.
      }
      // any other failure (still ambiguous) — leave untouched, try again next pass.
    }
  }
}


function _decodeJwt(token) {
  try {
    const part = token.split(".")[1];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch { return null; }
}

/** FP-031 — the stable identity to stamp/filter queue entries by.
 * Derived from the access token's own claims (sub/tenant_id), which
 * stay stable across token refresh for the same login session —
 * unlike the raw token string, which rotates. */
export function currentOwner() {
  if (typeof window === "undefined") return null;
  try {
    const token = window.localStorage.getItem("fop_bearer");
    if (!token) return null;
    const claims = _decodeJwt(token);
    if (!claims || !claims.sub || !claims.tenant_id) return null;
    return { tenant_id: claims.tenant_id, user_id: claims.sub };
  } catch { return null; }
}

function _sameOwner(a, b) {
  if (!a || !b) return true; // legacy entry with no owner stamp — adopt it
  return a.tenant_id === b.tenant_id && a.user_id === b.user_id;
}

function loadQueue() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

/** FP-032 fix: returns true/false instead of silently swallowing a
 * write failure. Callers (enqueue) MUST check this and surface a real
 * error rather than reporting "saved" when it wasn't durably queued. */
function saveQueue(q) {
  if (typeof window === "undefined") return false;
  let ok = true;
  try { window.localStorage.setItem(KEY, JSON.stringify(q)); }
  catch (e) { ok = false; }
  try {
    const owned = q.filter((e) => _sameOwner(e.owner, currentOwner()));
    window.dispatchEvent(new CustomEvent("forgedops:offline-queue", {
      detail: { count: owned.length, dead: owned.filter((e) => e.permanent).length, persistFailed: !ok },
    }));
  } catch {}
  return ok;
}

/** Synchronous, atomic (no `await` inside — JS's single-threaded
 * execution makes this a safe transaction against concurrent
 * enqueue()/flush() calls) read-modify-write helpers. FP-033 fix:
 * flush() must NEVER overwrite the whole queue with a start-of-loop
 * snapshot — every mutation re-reads the latest on-disk state first,
 * so a concurrent enqueue() that landed mid-flush is never lost. */
function _removeFromQueue(id) {
  const q = loadQueue().filter((e) => e.id !== id);
  return saveQueue(q);
}
function _updateEntryInQueue(id, patch) {
  const q = loadQueue();
  const idx = q.findIndex((e) => e.id === id);
  if (idx === -1) return false;
  q[idx] = { ...q[idx], ...patch };
  return saveQueue(q);
}

export function enqueue(op, payload) {
  const q = loadQueue();
  const entry = {
    id: `q_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
    op, payload,
    queued_at: new Date().toISOString(),
    attempts: 0,
    permanent: false,
    owner: currentOwner(),
    client_op_id: (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cop_${Date.now()}_${Math.floor(Math.random() * 1e9)}`,
  };
  q.push(entry);
  const persisted = saveQueue(q);
  if (!persisted) {
    // FP-032: never report success when the write didn't actually land.
    throw new Error(
      "This device could not save your work (local storage is full or " +
      "unavailable). It has NOT been queued — please free up space or " +
      "retry, and do not navigate away until you see it saved.",
    );
  }
  // Kick a flush attempt soon (microtask).
  setTimeout(flush, 0);
  return entry.id;
}

export function queueState() {
  const owner = currentOwner();
  const q = loadQueue().filter((e) => _sameOwner(e.owner, owner));
  return {
    count: q.length,
    pending: q.filter((e) => !e.permanent).length,
    dead: q.filter((e) => e.permanent).length,
  };
}


async function applyOne(entry) {
  switch (entry.op) {
    case "markup.create": {
      const { projectId, sheetId, body } = entry.payload || {};
      if (!projectId || !sheetId || !body) throw new Error("invalid payload");
      // FP-034 — client_op_id lets the server recognize a retried
      // create (response lost after the server already applied it)
      // instead of creating a duplicate markup.
      const r = await api.post(`/projects/${projectId}/sheets/${sheetId}/markups`,
        { ...body, client_op_id: entry.client_op_id });
      return r.data;
    }
    case "markup.delete": {
      const { markupId } = entry.payload || {};
      if (!markupId) throw new Error("invalid payload");
      try {
        await api.delete(`/markups/${markupId}`);
      } catch (e) {
        if (e?.response?.status === 404) return null; // already gone
        throw e;
      }
      return null;
    }
    // Iter 75 Phase 4 — expanded field operations.
    case "object.create": {
      const { projectId, body } = entry.payload || {};
      if (!projectId || !body) throw new Error("invalid payload");
      // FP-034 — client_op_id so a retried flush never creates a
      // duplicate field object.
      const r = await api.post(`/projects/${projectId}/objects`,
        { ...body, client_op_id: entry.client_op_id });
      return r.data;
    }
    case "object.update": {
      const { objectId, body } = entry.payload || {};
      if (!objectId || !body) throw new Error("invalid payload");
      const r = await api.patch(`/objects/${objectId}`, body);
      return r.data;
    }
    case "object.delete": {
      const { objectId } = entry.payload || {};
      if (!objectId) throw new Error("invalid payload");
      try { await api.delete(`/objects/${objectId}`); }
      catch (e) { if (e?.response?.status === 404) return null; throw e; }
      return null;
    }
    case "object.verify": {
      const { objectId } = entry.payload || {};
      if (!objectId) throw new Error("invalid payload");
      const r = await api.post(`/objects/${objectId}/verify`);
      return r.data;
    }
    case "object.reject": {
      const { objectId } = entry.payload || {};
      if (!objectId) throw new Error("invalid payload");
      const r = await api.post(`/objects/${objectId}/reject`);
      return r.data;
    }
    case "object.geometry.update": {
      const { objectId, body } = entry.payload || {};
      if (!objectId || !body) throw new Error("invalid payload");
      const r = await api.patch(`/objects/${objectId}/geometry`, body);
      return r.data;
    }
    case "pin.create": {
      const { sheetId, body } = entry.payload || {};
      if (!sheetId || !body) throw new Error("invalid payload");
      // FP-034 — fixed a dead route (this previously posted to
      // /projects/{projectId}/sheets/{sheetId}/pins, which was never
      // implemented on the backend and would 404) and added
      // client_op_id so a retried flush never creates a duplicate pin.
      const r = await api.post(`/pins`,
        { sheet_id: sheetId, ...body, client_op_id: entry.client_op_id });
      return r.data;
    }
    // ── Iter 85 Phase 7 — Offline punch engine ─────────────────────────
    case "punch.create": {
      const { projectId, body } = entry.payload || {};
      if (!projectId || !body) throw new Error("invalid payload");
      // FP-034 — see markup.create note above.
      const r = await api.post(`/projects/${projectId}/punch-items`,
        { ...body, client_op_id: entry.client_op_id });
      return r.data;
    }
    case "punch.update": {
      const { punchId, body } = entry.payload || {};
      if (!punchId || !body) throw new Error("invalid payload");
      const r = await api.patch(`/punch-items/${punchId}`, body);
      return r.data;
    }
    case "punch.status": {
      const { punchId, status } = entry.payload || {};
      if (!punchId || !status) throw new Error("invalid payload");
      const r = await api.post(`/punch-items/${punchId}/status`, { status });
      return r.data;
    }
    case "punch.comment": {
      const { punchId, body } = entry.payload || {};
      if (!punchId || !body) throw new Error("invalid payload");
      const r = await api.post(`/punch-items/${punchId}/comments`, body);
      return r.data;
    }
    case "pin.update": {
      const { pinId, body } = entry.payload || {};
      if (!pinId || !body) throw new Error("invalid payload");
      const r = await api.patch(`/pins/${pinId}`, body);
      return r.data;
    }
    case "comment.create": {
      const { url, body } = entry.payload || {};
      if (!url || !body) throw new Error("invalid payload");
      // FP-034 — client_op_id so a retried flush never creates a
      // duplicate comment (applies to both pin and punch comments,
      // which share this generic dispatch since both ultimately
      // accept a client_op_id field on their respective models).
      const r = await api.post(url, { ...body, client_op_id: entry.client_op_id });
      return r.data;
    }
    case "photo.upload": {
      // GH-32 (real-plan audit, 2026-10-01) — this used to dispatch to
      // `/projects/{projectId}/photos`, a route that has never existed
      // on any currently-deployed version of `routers/photos.py` (every
      // real photo endpoint is sheet-scoped and requires normalized_x /
      // normalized_y / origin_type, none of which this legacy payload
      // even supplied). Nothing in the app enqueues this op anymore —
      // field photo capture is `PhotoTimeline.jsx`'s "photo.capture" via
      // `lib/syncQueue.js`, which correctly targets the real
      // `/sheets/{sheet_id}/photos` endpoint. A real field device that
      // captured a photo offline before that migration could still be
      // holding one of these exact entries in its IndexedDB today — for
      // that case, fail loudly and PERMANENTLY right here (an axios-
      // shaped error with a 4xx `response.status`) so the existing
      // GH-07 "permanent 4xx, never silently dropped, never retried
      // forever" contract in `flush()` above takes over immediately:
      // the entry is flagged `permanent` and surfaced in the Offline
      // Queue panel (discardable there via "Drop failed") — instead of
      // either silently 404ing against a dead URL forever, or throwing
      // a plain Error (no `.response.status`) that would retry every
      // 30s, forever, and never succeed either way.
      const err = new Error(
        "This photo was captured by an older version of the app and can no longer be uploaded automatically. Please retake it from the sheet viewer.",
      );
      err.response = { status: 410 };
      throw err;
    }
    case "inspection.action": {
      const { url, body, method = "POST" } = entry.payload || {};
      if (!url) throw new Error("invalid payload");
      const r = await api.request({ url, method, data: body || {} });
      return r.data;
    }
    case "workflow.action": {
      const { url, body, method = "POST" } = entry.payload || {};
      if (!url) throw new Error("invalid payload");
      const r = await api.request({ url, method, data: body || {} });
      return r.data;
    }
    case "field.ack": {
      const { url, body } = entry.payload || {};
      if (!url) throw new Error("invalid payload");
      const r = await api.post(url, body || {});
      return r.data;
    }
    default:
      throw new Error(`unknown op ${entry.op}`);
  }
}

export async function flush() {
  if (_running) return { succeeded: 0, failed: 0 };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { succeeded: 0, failed: 0 };
  const owner = currentOwner();
  if (!owner) return { succeeded: 0, failed: 0 }; // FP-030/031: no valid session — nothing is safe to replay yet.
  _running = true;
  let succeeded = 0;
  let failed = 0;
  const syncedOps = [];
  try {
    // B2-08 — retry any previously-failed compensating DELETE first,
    // on every pass, independent of the main queue below (the
    // original create entry is long gone from the queue by the time
    // this is needed — it only ever lived in the tombstone).
    // B3-06 — resolve any create whose OWN outcome is still ambiguous
    // (no result_id yet) BEFORE attempting compensating deletes, so a
    // create that actually landed gets its delete attempted in the
    // SAME pass instead of waiting one more cycle.
    await _retryAmbiguousCreates(owner);
    await _retryPendingCancellations(owner);
    // FP-031: only ever act on entries owned by the CURRENTLY logged-in
    // identity. A snapshot is taken here purely to decide the WORK
    // LIST for this pass — every actual mutation below re-reads the
    // latest on-disk state first (see _removeFromQueue/_updateEntryInQueue),
    // so a concurrent enqueue() landing mid-flush is never lost
    // (FP-033), and a different identity's entries are never touched.
    const snapshot = loadQueue().filter((e) => _sameOwner(e.owner, owner));
    for (const entry of snapshot) {
      if (entry.permanent) continue;
      // R-04 — mark it in-flight BEFORE sending, so `dropEntry` can see
      // (via the durable, re-read-every-time queue, not this snapshot)
      // that a create for this exact entry is already on the wire the
      // instant the user presses Undo.
      _updateEntryInQueue(entry.id, { in_flight: true });
      try {
        const result = await applyOne(entry);
        _removeFromQueue(entry.id); // atomic re-read-filter-save, not a stale overwrite
        // R-04 — the entry is gone from the queue either way (its
        // local lifecycle is over); whether it was ALSO cancelled
        // while this create was in flight decides what happens to the
        // object the server just handed back.
        if (result && result.id && _COMPENSATING_DELETE[entry.op] && _loadTombstones()[entry.client_op_id]) {
          // B2-08 — do NOT pop the tombstone yet; it must survive a
          // failed/lost-response compensating DELETE so it can be
          // retried. Only a CONFIRMED delete (2xx, or 404 meaning
          // "already gone") clears it.
          _markTombstonePending(entry.client_op_id, result.id);
          try {
            await _COMPENSATING_DELETE[entry.op](result);
            _popTombstone(entry.client_op_id);
          } catch (e) {
            if (e?.response?.status === 404) {
              _popTombstone(entry.client_op_id);
            }
            // Any other failure: the tombstone stays, now carrying
            // `result_id`, so `_retryPendingCancellations` (called at
            // the top of every flush pass) retries the exact same
            // idempotent DELETE until it is actually confirmed.
          }
          continue; // never reaches the UI as a "synced" success.
        }
        succeeded += 1;
        syncedOps.push(entry.op);
      } catch (e) {
        const code = e?.response?.status;
        // B3-06 (2026-10-01 completion directive) — the create request
        // throwing does NOT prove the server never processed it; the
        // response could have been lost on the way back after the
        // server already committed (the exact real-world case a flaky
        // mobile/field connection produces constantly). Only a
        // DEFINITIVE, server-confirmed rejection (the same 4xx that
        // marks the entry `permanent` just below — the create will
        // NEVER land, on this or any future retry) makes it safe to
        // drop a pending cancellation tombstone here. Every other
        // failure leaves the tombstone exactly as it was: the entry
        // stays queued (not removed — see below) and is retried
        // idempotently (client_op_id) on the next pass, which will
        // either hand back the object that already landed — triggering
        // the normal pending-compensating-delete path above — or
        // genuinely create it only now. Either way the ambiguity is
        // resolved by an actual confirmed outcome, never assumed away.
        if (code && code >= 400 && code < 500 && code !== 429) {
          _popTombstone(entry.client_op_id); // confirmed: this create will never land — nothing to compensate.
        }
        // FP-030: an expired/invalid session (401) must NEVER cost the
        // user their queued work. Stop this flush pass entirely and
        // leave every remaining entry exactly as it was — the next
        // tick, after the user reauthenticates, will retry cleanly.
        if (code === 401) break;
        failed += 1;
        const attempts = (entry.attempts || 0) + 1;
        const patch = {
          attempts,
          last_error: String(code || e?.message || e).slice(0, 200),
        };
        // GH-07 — 4xx other than 401/404/429 is the ONLY actionable,
        // non-retryable rejection; mark it `permanent` explicitly so it
        // stops being retried, kept visible in the queue, never
        // silently dropped. Every OTHER failure (5xx, network error,
        // timeout — anything transient) only ever increments `attempts`
        // for display and is retried again next tick, no matter how
        // many times it has already failed — an extended outage must
        // self-heal the moment connectivity returns, with zero manual
        // action required.
        if (code && code >= 400 && code < 500 && code !== 429) {
          patch.permanent = true;
        }
        _updateEntryInQueue(entry.id, patch);
        // Abort the loop on the first error; we'll retry the rest on next tick.
        break;
      }
    }
  } finally {
    _running = false;
  }
  // Founder correction (2026-09-09) — "successful synchronization
  // updates the displayed record correctly" must hold for AUTOMATIC
  // background sync too, not only a manual "sync now" click. Every
  // caller of flush() (the 30s ticker, the `online` listener, and
  // syncTruth.js's drainAll()) now gets a real per-op-type signal to
  // trigger UI reconciliation, instead of that signal existing only
  // behind SyncBadge's manual button.
  if (succeeded > 0 && typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("forgedops:offline-synced", {
        detail: { succeeded, ops: syncedOps },
      }));
    } catch {}
  }
  return { succeeded, failed };
}

export function start() {
  if (typeof window === "undefined") return;
  if (_tick) return;
  window.addEventListener("online", flush);
  _tick = window.setInterval(flush, 30000);
  // Initial flush attempt.
  setTimeout(flush, 200);
}

// Iter 75 Phase 4 — recovery + introspection surfaces for the UI.
// FP-031: every surface below only ever exposes/mutates the CURRENTLY
// logged-in identity's own entries — a different identity's queued
// work on the same device is neither visible nor droppable/retryable
// from here.
export function listQueue() {
  const owner = currentOwner();
  return loadQueue().filter((e) => _sameOwner(e.owner, owner));
}
export function retryDead() {
  const owner = currentOwner();
  const q = loadQueue();
  let changed = false;
  for (const e of q) {
    if (_sameOwner(e.owner, owner) && e.permanent) {
      e.attempts = 0;
      e.permanent = false;
      e.last_error = null;
      changed = true;
    }
  }
  if (changed) saveQueue(q);
  setTimeout(flush, 0);
}
export function dropEntry(id) {
  const owner = currentOwner();
  const q = loadQueue();
  const removed = q.find((e) => e.id === id && _sameOwner(e.owner, owner)) || null;
  // R-04 — if a compensable create is ALREADY in flight (flush() has
  // sent it and is awaiting the response), removing it from the queue
  // here is necessary for Undo to feel instant, but it is NOT proof
  // the server never saw it — the create may complete successfully
  // moments from now regardless of this removal. Tombstone it so
  // flush() can issue a compensating delete against the EXACT object
  // the server hands back, instead of leaving a resurrected object no
  // local state remembers anything about.
  let tombstonePersisted = true;
  if (removed && removed.in_flight && _COMPENSATING_DELETE[removed.op]) {
    tombstonePersisted = _addTombstone(removed.client_op_id, removed.op, removed.owner || owner, removed.payload);
  }
  saveQueue(q.filter((e) => !(e.id === id && _sameOwner(e.owner, owner))));
  // B3-06 (2026-10-01 completion directive) — `removed` keeps its exact
  // original shape (restoreEntry depends on that, see GH-11 above);
  // the persistence status rides alongside it instead of mutating the
  // entry, so a caller that only wants `removed` back is unaffected.
  return removed ? { ...removed, _tombstonePersisted: tombstonePersisted } : null;
}
// GH-11 (real-plan certification, 2026-09-30) — "Drop" previously
// persisted a permanent, unrecoverable deletion on a single tap. This
// restores the EXACT entry object `dropEntry`/`dropAllDead` returned
// (same id/payload/client_op_id — idempotency on the server side is
// unaffected since the deleted entry was never actually sent), giving
// the UI a genuine undo, not just a re-queue-from-scratch.
export function restoreEntry(entry) {
  if (!entry || !entry.id) return;
  const owner = currentOwner();
  if (!_sameOwner(entry.owner, owner)) return;
  // R-04 — "undo the undo". If the original create was still in
  // flight when it got dropped, a tombstone is pending for it right
  // now; restoring means the user wants it kept after all, so clear
  // the tombstone before flush() ever gets a chance to act on it. The
  // re-queued entry below also gets its OWN turn through `flush()` —
  // harmless even if the original in-flight request lands first,
  // since both carry the same `client_op_id` and the server treats a
  // retried create with the same id as the same create, never a
  // duplicate.
  _popTombstone(entry.client_op_id);
  const q = loadQueue();
  if (q.some((e) => e.id === entry.id)) return; // already there — no-op
  // B3-06: strip the UI-facing `_tombstonePersisted` hint `dropEntry`
  // may have attached — it is not part of the queue entry's own shape
  // and must never be written back into storage.
  // eslint-disable-next-line no-unused-vars
  const { _tombstonePersisted, ...entryToRestore } = entry;
  // Only normalize `in_flight` back to false if the dropped entry
  // actually had it set — an entry that was never in flight never had
  // this key at all, and restoreEntry must put back the EXACT same
  // entry `dropEntry` removed (see the GH-11 regression test), not a
  // new shape with an extra key.
  q.push(entryToRestore.in_flight ? { ...entryToRestore, in_flight: false } : entryToRestore);
  saveQueue(q);
}
export function dropAllDead() {
  const owner = currentOwner();
  const q = loadQueue();
  const removed = q.filter((e) => _sameOwner(e.owner, owner) && e.permanent);
  saveQueue(q.filter((e) => !(_sameOwner(e.owner, owner) && e.permanent)));
  return removed;
}
